import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import UserGuideStep from "../../shared/components/docs/UserGuideStep";
import { H1Header } from "../../shared/components/others";

const Docs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const docsData = [
    {
      name: t("Overview", "Vue d'ensemble"),
      queryKey: "overview",
    },
    {
      name: t("Get-started-guide", "Guide de démarrage"),
      queryKey: "get-started-guide",
      children: [
        {
          icon: <ChevronRightIcon />,
          name: t("guide-content-title-1", "Introduction à BotBlogR"),
          queryKey: "introduction",
        },
        {
          icon: <ChevronRightIcon />,
          name: t("guide-content-title-2", "Intégrer sa clé OpenAI"),
          queryKey: "integrate-openai",
        },
        {
          icon: <ChevronRightIcon />,
          name: t(
            "guide-content-title-3",
            "Style d'écriture, identité et produits"
          ),
          queryKey: "generation-variables",
        },
        {
          icon: <ChevronRightIcon />,
          name: t("guide-content-title-4", "Projets"),
          queryKey: "projects",
        },
        {
          icon: <ChevronRightIcon />,
          name: t("guide-content-title-5", "Éditeur d'articles"),
          queryKey: "post-editor",
        },
        {
          icon: <ChevronRightIcon />,
          name: t("guide-content-title-6", "Centre d'aide & feedbacks"),
          queryKey: "feedbacks",
        },
      ],
    },
    {
      name: t("Integrations", "Intégrations"),
      queryKey: "integrations",
      children: [
        {
          icon: <ChevronRightIcon />,
          name: t("openai", "OpenAI"),
          queryKey: "openai",
        },
        {
          icon: <ChevronRightIcon />,
          name: t("perplexity", "Perplexity"),
          queryKey: "perplexity",
        },
      ],
    },
    {
      name: t("Projects", "Projets"),
      queryKey: "projets",
      children: [
        {
          icon: <ChevronRightIcon />,
          name: t("wordpress", "Wordpress"),
          queryKey: "wordpress",
        },
        {
          icon: <ChevronRightIcon />,
          name: t("shopify", "Shopify"),
          queryKey: "shopify",
        },
        {
          icon: <ChevronRightIcon />,
          name: t("wix", "Wix"),
          queryKey: "wix",
        },
      ],
    },
  ];

  const guideContent = [
    {
      step: 1,
      title: t("guide-content-title-1", "Introduction à BotBlogR"),
      subtitle: t(
        "guide-content-subtitle-1",
        "Les bases à savoir sur le logiciel, son organisation et son fonctionnement"
      ),
      text: t(
        "guide-content-text-intro",
        "Bonjour et bienvenu ! Dans cette première vidéo, vous allez découvrir les différents éléments qui composent la plateforme BotBlogR."
      ),
      link: "https://www.youtube.com/embed/2_fCUa9G_dw",
    },
    {
      step: 2,
      title: t("guide-content-title-2", "Intégrer sa clé OpenAI"),
      subtitle: t("guide-content-subtitle-2", "Pour commencer à générer"),
      text: t(
        "guide-content-text-link-openai",
        "Pour pouvoir utiliser les fonctions d'IA générative de BotBlogR, vous devez intégrer votre clé API OpenAI. Dans cette vidéo, vous allez découvrir qu'est ce qu'une clé API, comment la créer en moins de 5 minutes et comment l'ajouter à BotBlogR en un click. Voici le lien vers la plateforme OpenAI : https://platform.openai.com/docs/overview"
      ),
      link: "https://www.youtube.com/embed/Iz2cOGj01kE",
    },
    {
      step: 3,
      title: t(
        "guide-content-title-3",
        "Style d'écriture, identité et produits"
      ),
      subtitle: t(
        "guide-content-subtitle-generation-variables",
        "Découvrez comment customiser les fonctions d'IA de notre outil"
      ),
      text: t(
        "guide-content-text-generation-variables",
        "Customiser les fonctions génératives est un élément important pour créer un contenu cohérent et adapté à son site web. Dans cette vidéo, vous allez voir comment rapidement personnaliser le style d'écriture et le persona de rédacteur de l'IA générative via notre système de variables de génération."
      ),
      link: "https://www.youtube.com/embed/SUfBxjefoTg",
    },
    {
      step: 4,
      title: t("guide-content-title-projects", "Projets"),
      subtitle: t(
        "guide-content-subtitle-projects",
        "Découvrez comment lier votre site web à BotBlogR et publier en un click"
      ),
      text: t(
        "guide-content-text-projects",
        "Dans cette vidéo, vous allez découvrir le fonctionnement des projets, qui vous permettent de connecter votre site web via votre CMS pour obtenir une personnalisation avancée et la possibilité de publier vos articles de blog en un click !"
      ),
      link: "https://www.youtube.com/embed/hfEVd99P7rM",
    },
    {
      step: 5,
      title: t("guide-content-title-post-editor", "Éditeur d'article"),
      subtitle: t(
        "guide-content-subtitle-post-editor",
        "Apprenez tout ce qu'il y a à savoir sur l'éditeur d'article de blog"
      ),
      text: t(
        "guide-content-text-post-editor-AI-only",
        "Dans cette dernière vidéo, vous allez découvrir tout ce qu'il faut savoir sur l'éditeur d'article de blog de BotBlogR, avec un cas pratique de création d'article de A à 2 pour vous familiariser avec l'outil."
      ),
      link: "https://www.youtube.com/embed/Lg92kCIwcGs",
      linkShort: "https://www.youtube.com/embed/3B2637q-JDg",
    },
    {
      step: 6,
      title: t("guide-content-title-feedbacks", "Centre d'Aide et Feedbacks"),
      subtitle: t(
        "guide-content-subtitle-feedbacks",
        "Apprenez-en plus sur notre système d'aide et de feedbacks"
      ),
      text: t(
        "guide-content-text-feedbacks",
        "Besoin d'aide ? De signaler un bug ? D'une nouvelle fonctionnalité ? Découvrez dans cette vidéo tout ce qu'il y a à savoir sur notre système d'aide et de feedbacks et comment vous pouvez facilement participer à l'amélioration de l'outil !"
      ),
      link: "https://www.youtube.com/embed/j5fadQgLJDQ",
    },
  ];

  const integrationsContent = [
    {
      step: 1,
      title: t("openai", "OpenAI"),
      subtitle: "",
      text: t(
        "integration-content-text-openai",
        "Pour pouvoir utiliser les fonctions d'IA générative de BotBlogR, vous devez intégrer votre clé API OpenAI. Dans cette vidéo, vous allez découvrir qu'est ce qu'une clé API, comment la créer en moins de 5 minutes et comment l'ajouter à BotBlogR en un click. Voici le lien vers la plateforme OpenAI : https://platform.openai.com/docs/overview"
      ),
      link: "https://www.youtube.com/embed/fDebDUvmxDg",
    },
    {
      step: 2,
      title: t("perplexity", "Perplexity"),
      subtitle: "",
      text: t(
        "integration-content-text-perplexity",
        "Pour utiliser certaines fonctionnalités supplémentaires de BotBlogR, ou tout simplement pour profiter d’une plus grande variété d’options de génération, vous pouvez intégrer Perplexity, un autre outil d’IA permettant de facilement récolter des informations en direct depuis le web. Dans cette vidéo, vous allez découvrir comment créer sa clé API Perplexity en moins de 5 minutes et comment l'ajouter à BotBlogR en un click. Voici le lien vers la plateforme Perplexity : https://docs.perplexity.ai/home"
      ),
      link: "https://www.youtube.com/embed/srRsa93iUok",
    },
  ];

  const projectsContent = [
    {
      step: 1,
      title: t("wordpress", "Wordpress"),
      subtitle: "",
      text: t(
        "project-content-text-for-wordpress",
        "<p>Découvre comment connecter ton site wordpress à BotBlogR. Grâce à cela, tu vas pouvoir exporter et publier tes contenus automatiquement, en plus d'avoir accès à plein d'autres outils de prévisualisation et d'automatisation.</p><p>POUR LES PERSONNES N'AYANT PAS ACCÈS À L'ÉDITEUR DE FICHIERS DE THÈME WORDPRESS :</p><ul><li>À rechercher dans le fichier : <pre>DISALLOW_FILE_EDIT</pre></li><li>À ajouter/modifier dans le fichier : <pre>define('DISALLOW_FILE_EDIT', false);</pre></li></ul>"
      ),
      link: "https://www.youtube.com/embed/0-G68UBe2L8",
    },
    {
      step: 2,
      title: t("shopify", "Shopify"),
      subtitle: "",
      text: t(
        "project-content-text-for-shopify",
        "<p>Découvre comment connecter ta boutique shopify à BotBlogR. Grâce à cela, tu vas pouvoir exporter et publier tes contenus automatiquement, en plus d'avoir accès à plein d'autres outils de prévisualisation et d'automatisation.</p>"
      ),
      link: "https://www.youtube.com/embed/q2aWxqlDSQ4",
    },
    {
      step: 3,
      title: t("wix", "Wix"),
      subtitle: "",
      text: t(
        "project-content-text-for-wix",
        "<p>Découvre comment connecter ton site Wix à BotBlogR. Grâce à cela, tu vas pouvoir exporter et publier tes contenus automatiquement, en plus d'avoir accès à plein d'autres outils de prévisualisation et d'automatisation.</p>"
      ),
      link: "https://www.youtube.com/embed/FbRCMmQxlYM",
    },
  ];

  const [selectedField, setSelectedField] = useState(0);
  const [selectedChildField, setSelectedChildField] = useState(-1);
  const [selected, setSelected] = useState(docsData[0].name);
  const [openTools, setOpenTools] = useState(false);

  const query = new URLSearchParams(location.search);
  const field = query.get("menu") || "overview";
  const childField = query.get("submenu") || null;

  useEffect(() => {
    if (field) {
      const fieldIndex = docsData.findIndex((item) => item.queryKey === field);
      handleMainToolsClick({ item: docsData[fieldIndex], index: fieldIndex });

      if (childField) {
        const subFieldIndex = docsData[fieldIndex].children.findIndex(
          (item) => item.queryKey === childField
        );
        handleChildToolsClick({ childIndex: subFieldIndex });
      } else {
        setSelectedChildField(-1);
      }
    }
  }, [field, childField]);

  const handleMainToolsClick = ({ item, index }) => {
    setSelectedField(index);
    if (item?.children) {
      setOpenTools(true);
    } else {
      setOpenTools(false);
    }
    setSelectedChildField(-1);
    setSelected(item.name);
  };

  const handleChildToolsClick = ({ childIndex }) => {
    setSelectedChildField(childIndex);
  };

  const renderSubMenu = (children) => {
    return (
      <>
        {children.map((child, childIndex) => (
          <div
            className={`flex  bg-white justify-center`}
            key={childIndex}
            onClick={() =>
              navigate(
                `/docs?menu=${docsData[selectedField].queryKey}&submenu=${child.queryKey}`
              )
            }
          >
            <button
              className={`pl-6 text-[15px] ${
                selectedChildField === childIndex
                  ? "text-blue-50"
                  : "text-black"
              } w-full py-2 flex  gap-3`}
            >
              {child.icon} {child.name}
            </button>
          </div>
        ))}
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t("docs", "Documentations")} | BotBlogR</title>
        <meta
          name="description"
          content="Page de documentations de la plateforme."
        />
      </Helmet>
      <div className="px-2 pl-6 pr-6">
        <div className="flex items-center justify-between py-3">
          <H1Header title={"Documents"} />
        </div>
        <div className="flex gap-2 w-full">
          <div className="bg-white min-w-[350px] shadow-default px-5 py-4 pb-5 sm:px-7.5 rounded-lg lg:h-[calc(100vh-230px)] overflow-y-auto">
            <ul className="font-medium sidbar_menu lg:block hidden">
              {docsData?.map((item, index) => {
                return (
                  <div
                    className={`${
                      selectedField === index ? "text-blue-50" : "text-black"
                    }`}
                    key={index}
                  >
                    <p
                      onClick={() => navigate(`/docs?menu=${item?.queryKey}`)}
                      className="mb-2 text-xl font-semibold cursor-pointer"
                    >
                      {item?.name}
                    </p>

                    {item?.children && (
                      <ul>
                        {item?.children &&
                          openTools &&
                          item.name === selected &&
                          renderSubMenu(item?.children)}
                      </ul>
                    )}
                  </div>
                );
              })}
            </ul>

            <div className="lg:hidden block">
              <Listbox value={selected} onChange={setSelected}>
                <div className="relative mt-2">
                  <ListboxButton className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                    <span className="flex items-center">
                      <span className="ml-3 block truncate">{selected}</span>
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      {/* <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" /> */}
                    </span>
                  </ListboxButton>

                  <ListboxOptions
                    transition
                    className="z-10 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                  >
                    {docsData?.map((item, index) => (
                      <ListboxOption
                        key={index}
                        value={item.name}
                        className="group relative cursor-default select-none py-2 text-gray-900"
                      >
                        <div className=" items-center">
                          <span
                            onClick={() =>
                              navigate(`/docs?menu=${item?.queryKey}`)
                            }
                            className="ml-3 block truncate font-normal group-data-[selected]:font-semibold"
                          >
                            {item?.name}
                          </span>

                          {item?.children && (
                            <ul>
                              {item?.children &&
                                openTools &&
                                selected === item.name &&
                                renderSubMenu(item?.children)}
                            </ul>
                          )}
                        </div>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
          </div>
          <div className="bg-white px-5 pb-5 sm:px-7.5 shadow-default rounded-lg lg:h-[calc(100vh-230px)] overflow-y-auto flex-1">
            {/* OVERVIEW */}
            {selected === t("Overview", "Vue d'ensemble") && (
              <div className="max-w-[950px] mx-auto pt-4">
                <div className="grid xl:grid-cols-5 col-span-5 md:gap-5 relative">
                  <div className="xl:col-span-5 col-span-5 ">
                    <div className="rounded-lg pt-1 pb-0  h-full group">
                      <div className="px-4">
                        <div
                          className={`px-2 py-1 my-3 mt-0 relative text-black}`}
                          key={"overview-content"}
                        >
                          <h2 className="font-semibold text-2xl">
                            {t("Overview", "Vue d'ensemble")}
                          </h2>
                        </div>
                        <p className="text-base my-5 min-h-[71px]">
                          {t(
                            "overview-text-docs",
                            "Bienvenue dans la documentation de BotBlogR. Cette section vous donnera un aperçu des fonctionnalités principales."
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* GET STARTED GUIDE */}
            {selected === t("Get-started-guide", "Guide de démarrage") &&
              selectedChildField === -1 && (
                <div className="max-w-[950px] mx-auto pt-4">
                  <div className="grid xl:grid-cols-5 col-span-5 md:gap-5 relative">
                    <div className="xl:col-span-5 col-span-5 ">
                      <div className="rounded-lg pt-1 pb-0  h-full group">
                        <div className="px-4">
                          <div
                            className={`px-2 py-1 my-3 mt-0 relative text-black}`}
                            key={"overview-content"}
                          >
                            <h2 className="font-semibold text-2xl">
                              {t("Get-started-guide", "Guide de démarrage")}
                            </h2>
                          </div>
                          <p className="text-base my-5 min-h-[71px]">
                            {t(
                              "get-started-guide-start-text",
                              "Commencez ici via une courte série de vidéo vous présentant toutes les fonctionnalités et options disponibles sur BotBlogR."
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            {selectedField === 1 &&
              selectedChildField !== -1 &&
              selected === t("Get-started-guide", "Guide de démarrage") && (
                <UserGuideStep data={guideContent[selectedChildField]} />
              )}

            {/* INTEGRATIONS */}
            {selected === t("Integrations", "Intégrations") &&
              selectedChildField === -1 && (
                <div className="max-w-[950px] mx-auto pt-4">
                  <div className="grid xl:grid-cols-5 col-span-5 md:gap-5 relative">
                    <div className="xl:col-span-5 col-span-5 ">
                      <div className="rounded-lg pt-1 pb-0  h-full group">
                        <div className="px-4">
                          <div
                            className={`px-2 py-1 my-3 mt-0 relative text-black}`}
                            key={"overview-content"}
                          >
                            <h2 className="font-semibold text-2xl">
                              {t("Integrations", "Intégrations")}
                            </h2>
                          </div>
                          <p className="text-base my-5 min-h-[71px]">
                            {t(
                              "integrations-text",
                              "Découvrez nos tutoriels d'intégration pour connecter facilement des services tiers à la plateforme (comme les outils d'IA type OpenAI, Perplexity etc.), avec des guides pas-à-pas adaptés aux experts aussi bien qu'aux débutants."
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            {selectedField === 2 &&
              selectedChildField !== -1 &&
              selected === t("Integrations", "Intégrations") && (
                <UserGuideStep data={integrationsContent[selectedChildField]} />
              )}

            {/* INTEGRATIONS */}
            {selected === t("Projects", "Projets") &&
              selectedChildField === -1 && (
                <div className="max-w-[950px] mx-auto pt-4">
                  <div className="grid xl:grid-cols-5 col-span-5 md:gap-5 relative">
                    <div className="xl:col-span-5 col-span-5 ">
                      <div className="rounded-lg pt-1 pb-0  h-full group">
                        <div className="px-4">
                          <div
                            className={`px-2 py-1 my-3 mt-0 relative text-black}`}
                            key={"overview-content"}
                          >
                            <h2 className="font-semibold text-2xl">
                              {t("Projects", "Projets")}
                            </h2>
                          </div>
                          <p className="text-base my-5 min-h-[71px]">
                            {t(
                              "projects-text",
                              "Découvrez nos tutoriels pour connecter facilement vos sites web via vos différents CMS (Wordpress, Shopify ...), avec des guides pas-à-pas adaptés aux experts aussi bien qu'aux débutants."
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            {selectedField === 3 &&
              selectedChildField !== -1 &&
              selected === t("Projects", "Projets") && (
                <UserGuideStep data={projectsContent[selectedChildField]} />
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Docs;
